/**
 * Crop and fit boxes in source-image pixels, the space the markup string's
 * `crop,{x}x{y},{w}x{h}` uses. A box that reaches past the photo (negative
 * x/y, or wider/taller than it) renders the overflow as bars.
 */

export type Size = { width: number; height: number };
export type Box = { x: number; y: number; width: number; height: number };
/** A fixed shape as whole units, e.g. 4:3. Boxes are whole multiples of it. */
export type Ratio = { x: number; y: number };
export type Placement = 'start' | 'center' | 'end';
export type CropMode = 'crop' | 'fit';

// Upload caps from Libs/Constants.php (IMAGE_MAX_DIMENSION, IMAGE_MAX_PIXELS);
// ImageCropFrameModule rejects a box past them.
const MAX_DIMENSION = 11_000;
const MAX_PIXELS = 80_000_000;

/** Crop removing more than this much of the photo opens on Fit (AUTO_PAD_THRESHOLD). */
const FIT_WHEN_CROP_REMOVES = 0.1;

const PLACEMENT_SHARE: Record<Placement, number> = { start: 0, center: 0.5, end: 1 };

/** The MediaFilters ratio names; anything else crops freely. */
export function ratioFromName(name: string | null | undefined): Ratio | null {
   switch (name) {
      case 'FOUR_THREE': {
         return { x: 4, y: 3 };
      }
      case 'ONE_ONE': {
         return { x: 1, y: 1 };
      }
      default: {
         return null;
      }
   }
}

/**
 * The biggest box of this shape inside the photo. Whole units keep it the
 * exact shape the filter checks for, as the legacy dialog's width-multiple-
 * of-4 snap did.
 */
export function largestCropSize(source: Size, ratio: Ratio): Size {
   const units = Math.floor(Math.min(source.width / ratio.x, source.height / ratio.y));
   return { width: units * ratio.x, height: units * ratio.y };
}

/** The smallest box of this shape that holds the whole photo. */
function fitSize(source: Size, ratio: Ratio): Size {
   const units = Math.ceil(Math.max(source.width / ratio.x, source.height / ratio.y));
   return { width: units * ratio.x, height: units * ratio.y };
}

/** A crop box of `size` centered on the photo. */
export function centeredCropBox(source: Size, size: Size): Box {
   return {
      x: Math.round((source.width - size.width) / 2),
      y: Math.round((source.height - size.height) / 2),
      width: size.width,
      height: size.height,
   };
}

/** Which way a fitted photo gets its bars: beside it, or above and below. */
export function barsAxis(source: Size, ratio: Ratio): 'x' | 'y' {
   return source.width * ratio.y > source.height * ratio.x ? 'y' : 'x';
}

export function fitBox(source: Size, ratio: Ratio, placement: Placement): Box {
   const size = fitSize(source, ratio);
   const axis = barsAxis(source, ratio);
   // Rounding to whole units can leave a pixel or two of bar on the other
   // axis too; that sliver stays centered.
   const shareX = axis === 'x' ? PLACEMENT_SHARE[placement] : 0.5;
   const shareY = axis === 'y' ? PLACEMENT_SHARE[placement] : 0.5;
   // `0 -` rather than unary minus, which turns a zero offset into -0.
   return {
      x: 0 - Math.round((size.width - source.width) * shareX),
      y: 0 - Math.round((size.height - source.height) * shareY),
      width: size.width,
      height: size.height,
   };
}

/** How much of the photo the biggest crop of this shape leaves out, 0 to 1. */
export function cropRemoves(source: Size, ratio: Ratio): number {
   const crop = largestCropSize(source, ratio);
   return 1 - (crop.width * crop.height) / (source.width * source.height);
}

function meetsMinimum(size: Size, min: Size): boolean {
   return size.width >= min.width && size.height >= min.height;
}

function withinCaps(size: Size): boolean {
   return (
      size.width <= MAX_DIMENSION &&
      size.height <= MAX_DIMENSION &&
      size.width * size.height <= MAX_PIXELS
   );
}

/** Whether Crop can produce a box this spot accepts. */
export function canCrop(source: Size, ratio: Ratio | null, min: Size): boolean {
   return meetsMinimum(ratio ? largestCropSize(source, ratio) : source, min);
}

/** Whether Fit can: the bars may bring a small photo up to size, but not past the caps. */
export function canFit(source: Size, ratio: Ratio | null, min: Size): boolean {
   if (!ratio) {
      return false;
   }
   const size = fitSize(source, ratio);
   return meetsMinimum(size, min) && withinCaps(size);
}

/**
 * The mode a photo opens in when it has no crop yet: Fit when cropping would
 * cut a lot or can't reach the minimum, Crop otherwise.
 */
export function defaultMode(source: Size, ratio: Ratio | null, min: Size): CropMode {
   if (!canFit(source, ratio, min)) {
      return 'crop';
   }
   if (!canCrop(source, ratio, min)) {
      return 'fit';
   }
   return ratio && cropRemoves(source, ratio) > FIT_WHEN_CROP_REMOVES ? 'fit' : 'crop';
}

export function reachesPastPhoto(box: Box, source: Size): boolean {
   return (
      box.x < 0 ||
      box.y < 0 ||
      box.x + box.width > source.width ||
      box.y + box.height > source.height
   );
}

export function hasRatio(box: Box, ratio: Ratio): boolean {
   return box.width * ratio.y === box.height * ratio.x;
}

/** The placement closest to where an existing fitted box puts the photo. */
export function placementOf(box: Box, source: Size, ratio: Ratio): Placement {
   const axis = barsAxis(source, ratio);
   const slack = axis === 'x' ? box.width - source.width : box.height - source.height;
   const offset = axis === 'x' ? -box.x : -box.y;
   if (slack <= 0) {
      return 'center';
   }
   const share = offset / slack;
   return share < 0.25 ? 'start' : share > 0.75 ? 'end' : 'center';
}

/**
 * Snap a box dragged in the crop view to whole units of the ratio, inside
 * the photo. Returns null when it would fall under the minimum.
 */
export function snapCropBox(box: Box, source: Size, ratio: Ratio | null, min: Size): Box | null {
   let width: number;
   let height: number;
   if (ratio) {
      const largest = largestCropSize(source, ratio);
      const units = Math.min(
         Math.round(Math.min(box.width / ratio.x, box.height / ratio.y)),
         largest.width / ratio.x
      );
      width = units * ratio.x;
      height = units * ratio.y;
   } else {
      width = Math.min(Math.round(box.width), source.width);
      height = Math.min(Math.round(box.height), source.height);
   }
   if (!meetsMinimum({ width, height }, min)) {
      return null;
   }
   return {
      x: clamp(Math.round(box.x), 0, source.width - width),
      y: clamp(Math.round(box.y), 0, source.height - height),
      width,
      height,
   };
}

/**
 * The box after a handle (a cropperjs resize action, e.g. 'ne-resize') is
 * dragged by (dx, dy) with Alt held: the opposite side moves the same
 * distance the other way, so the center stays put. A fixed shape follows
 * whichever side changed more. Growth stops where the box would reach past
 * `bounds`. Works in whatever pixels `start` and `bounds` share.
 */
export function resizeAroundCenter(
   start: Box,
   drag: { handle: string; dx: number; dy: number },
   bounds: Size,
   isShapeFixed: boolean
): Box {
   const side = drag.handle.replace('-resize', '');
   const xSign = side.includes('e') ? 1 : side.includes('w') ? -1 : 0;
   const ySign = side.startsWith('s') ? 1 : side.startsWith('n') ? -1 : 0;
   const centerX = start.x + start.width / 2;
   const centerY = start.y + start.height / 2;
   const maxWidth = 2 * Math.min(centerX, bounds.width - centerX);
   const maxHeight = 2 * Math.min(centerY, bounds.height - centerY);
   let width = Math.max(0, start.width + 2 * xSign * drag.dx);
   let height = Math.max(0, start.height + 2 * ySign * drag.dy);
   if (isShapeFixed) {
      const scaleX = width / start.width;
      const scaleY = height / start.height;
      const shouldFollowWidth =
         ySign === 0 || (xSign !== 0 && Math.abs(scaleX - 1) >= Math.abs(scaleY - 1));
      const scale = Math.min(
         shouldFollowWidth ? scaleX : scaleY,
         maxWidth / start.width,
         maxHeight / start.height
      );
      width = start.width * scale;
      height = start.height * scale;
   }
   // The scaled size can overshoot the limit by a rounding error, which
   // would put the box a hair past the photo's edge.
   width = Math.min(width, maxWidth);
   height = Math.min(height, maxHeight);
   return { x: centerX - width / 2, y: centerY - height / 2, width, height };
}

function clamp(value: number, low: number, high: number): number {
   return Math.min(Math.max(value, low), high);
}

/** The crop instruction for the markup string, without its semicolons. */
export function cropInstruction(box: Box, background: string | null): string {
   const color = background ? `,${background}` : '';
   return `crop,${box.x}x${box.y},${box.width}x${box.height}${color}`;
}
